import type { Scenario } from '../types';

export const HARD_NO: Scenario = {
  id: 'hard-no',
  title: 'the hard no',
  brief: 'you have named a hard limit. he is pushing back with reasons. the reasons are good. you still don\'t want it.',
  skill: 'holding a limit against intelligent argument — without owing him an argument back',
  estMinutes: 5,
  startNode: 'open',
  nodes: {
    open: {
      id: 'open',
      paragraphs: [
        { text: 'you have just told him a hard limit. he has heard you. you can see in his face that he is composing a response, not accepting it.', speaker: 'narration' },
        { text: '"i hear that," he says. "and i want to talk about it. i think i can offer some context that might make it land differently."', speaker: 'them' },
      ],
      choices: [
        { text: 'listen to his context. you can change your mind if it changes your mind.', nextNode: 'listen' },
        { text: '"it\'s a hard limit. context doesn\'t change it."', nextNode: 'firm' },
        { text: '"i don\'t want to talk about it more tonight."', nextNode: 'pause' },
      ],
    },
    listen: {
      id: 'listen',
      paragraphs: [
        { text: 'he gives you the context. it is thoughtful. it is well-argued. several of the things he says are true.', speaker: 'narration' },
        { text: 'you notice, partway through, that the truth of his points does not change the truth of your no.', speaker: 'narration' },
        { text: 'he finishes. he looks at you.', speaker: 'narration' },
      ],
      choices: [
        { text: '"the points you made are good. my answer is still no."', nextNode: 'firm-after-listening' },
        { text: 'soften: "let me think about what you said."', nextNode: 'soften' },
      ],
    },
    firm: {
      id: 'firm',
      paragraphs: [
        { text: 'you watch him register it. there is a pause that is almost two seconds long.', speaker: 'narration' },
        { text: '"okay," he says, eventually.', speaker: 'them' },
        { text: 'he does not say it warmly. he does not say it with resentment, either. he is honest about not liking the answer.', speaker: 'narration' },
      ],
      choices: [
        { text: 'let the silence stand. don\'t fill it.', nextNode: 'silence' },
        { text: 'acknowledge what he\'s feeling: "i can see this is hard for you."', nextNode: 'acknowledge' },
      ],
    },
    pause: {
      id: 'pause',
      paragraphs: [
        { text: 'he hears it. he does not push.', speaker: 'narration' },
        { text: '"okay. but i want you to know i\'m going to want to talk about it eventually. not tonight."', speaker: 'them' },
        { text: 'fair enough, you think. he is allowed to want to talk about it. you are allowed to want to not, tonight.', speaker: 'narration' },
      ],
      choices: [
        { text: 'agree to talk later. close the topic for tonight.', nextNode: 'later-close' },
      ],
    },
    'firm-after-listening': {
      id: 'firm-after-listening',
      paragraphs: [
        { text: 'he holds your gaze. for a moment, you can feel him deciding what to do with this.', speaker: 'narration' },
        { text: '"i hear you," he says. "i won\'t bring it up again unless you do."', speaker: 'them' },
        { text: 'you don\'t know yet if he means it. you will find out over the next weeks.', speaker: 'narration' },
      ],
      choices: [
        { text: 'thank him for hearing it.', nextNode: 'close-clean' },
      ],
    },
    soften: {
      id: 'soften',
      paragraphs: [
        { text: 'the words leave you before you mean them. you say "let me think about it" because it is easier than holding firm.', speaker: 'narration' },
        { text: 'he visibly relaxes. you watch him hear a small win.', speaker: 'narration' },
        { text: 'tonight, after he leaves, you will replay this conversation. you will notice the moment you softened.', speaker: 'narration' },
      ],
      choices: [
        { text: 'go back. correct it. "actually — i don\'t need to think about it. it\'s still a no."', nextNode: 'rescue' },
        { text: 'let the soft answer stand. plan to revisit tomorrow.', nextNode: 'softened' },
      ],
    },
    silence: {
      id: 'silence',
      paragraphs: [
        { text: 'the silence holds. neither of you fills it. after a long moment, he reaches for his glass.', speaker: 'narration' },
        { text: '"okay," he says again, more settled this time. "thank you for being clear."', speaker: 'them' },
      ],
      choices: [
        { text: 'change the subject yourself.', nextNode: 'close-clean' },
      ],
    },
    acknowledge: {
      id: 'acknowledge',
      paragraphs: [
        { text: '"it is. thank you for naming that."', speaker: 'them' },
        { text: 'his shoulders ease slightly. some of the weight of being told no metabolizes faster when someone notices the weight.', speaker: 'narration' },
      ],
      choices: [
        { text: 'leave it there. the topic is closed for tonight.', nextNode: 'close-clean' },
      ],
    },
    'later-close': {
      id: 'later-close',
      paragraphs: [
        { text: 'the rest of the evening is fine. the topic stays closed.', speaker: 'narration' },
        { text: 'you make a quiet note to yourself: he wanted to bring it back. that means it will come back. you will need to hold the line again, or change your mind on purpose. not by erosion.', speaker: 'narration' },
      ],
    },
    rescue: {
      id: 'rescue',
      paragraphs: [
        { text: 'you say it. the second sentence costs you something. it is also the right one.', speaker: 'narration' },
        { text: 'he accepts it. quietly, this time.', speaker: 'narration' },
        { text: '"okay. i appreciate the correction. won\'t bring it up again."', speaker: 'them' },
      ],
      choices: [
        { text: 'close cleanly.', nextNode: 'close-clean' },
      ],
    },
    softened: {
      id: 'softened',
      paragraphs: [
        { text: 'tomorrow you will need to bring this back up. that is your job, not his.', speaker: 'narration' },
        { text: 'tonight, you notice the cost of softening. the cost is that you will spend the next twenty-four hours composing what to say.', speaker: 'narration' },
      ],
    },
    'close-clean': {
      id: 'close-clean',
      paragraphs: [
        { text: 'later, you replay the conversation and find no place where you owe him a re-litigation. the no is the no.', speaker: 'narration' },
        { text: 'a hard limit you have to defend twice is still a hard limit.', speaker: 'narration' },
      ],
    },
  },
  reflection: {
    title: 'on holding the line against good arguments',
    skillPracticed: 'noticing that the rightness of an argument does not change the rightness of a no',
    body: 'one of the trickier patterns in dynamic-shaped relationships is being asked to defend a no with the same intellectual rigor as the request. but you do not owe the partner an argument. you owe them a clear answer. clarity is the gift. softening — "let me think about it" — is sometimes a true answer, but often it is a way of postponing the cost of holding firm. notice which one it is. the partner who hears a hard limit as data, not as a debate prompt, is the one who earns the right to keep being trusted with the softer ones.',
    otherPaths: [
      'you could have asked him to name what need is underneath his request. sometimes the underlying need can be met another way.',
      'you could have asked yourself why this is a hard limit — not to argue yourself out of it, but to know it better. some hard limits soften with time; many do not. honoring that is part of the practice.',
      'you could have noticed: if he had pushed harder, what would you have done? the answer is information.',
    ],
  },
};
